/**
 * Validation utility functions for form fields
 */

import { getDaysInMonth } from './dateUtils';

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates an email address
 */
export const validateEmail = (email: string): ValidationResult => {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  return { isValid: true };
};

/**
 * Validates a password
 */
export const PASSWORD_MIN_LENGTH = 6;
export const PASSWORD_MAX_LENGTH = 128;

export const validatePassword = (
  password: string,
  minLength: number = PASSWORD_MIN_LENGTH
): ValidationResult => {
  if (!password || password.trim() === '') {
    return { isValid: false, error: 'Password is required' };
  }

  if (password.length < minLength) {
    return {
      isValid: false,
      error: `Password must be at least ${minLength} characters long`,
    };
  }

  if (password.length > PASSWORD_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Password must be at most ${PASSWORD_MAX_LENGTH} characters long`,
    };
  }

  return { isValid: true };
};

/**
 * Validates password confirmation
 */
export const validatePasswordConfirmation = (
  password: string,
  confirmPassword: string
): ValidationResult => {
  if (!confirmPassword || confirmPassword.trim() === '') {
    return { isValid: false, error: 'Please confirm your password' };
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: 'Passwords do not match' };
  }

  return { isValid: true };
};

/**
 * Validates a required field
 */
export const validateRequired = (
  value: string | number | undefined | null,
  fieldName?: string
): ValidationResult => {
  if (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return {
      isValid: false,
      error: fieldName ? `${fieldName} is required` : 'This field is required',
    };
  }

  return { isValid: true };
};

/**
 * Validates a phone number (international / E.164-ish).
 * Empty is allowed when not required. A country code alone must not pass.
 */
export const validatePhone = (
  phone: string,
  required: boolean = false
): ValidationResult => {
  if (!phone || phone.trim() === '') {
    if (required) {
      return { isValid: false, error: 'Phone number is required' };
    }
    return { isValid: true }; // Optional field
  }

  const trimmed = phone.trim();
  // Basic phone validation - allows international formats
  const phoneRegex = /^[\d\s\-\+\(\)]+$/;
  if (!phoneRegex.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid phone number' };
  }

  // Remove non-digits and check length (E.164: max 15 digits)
  const digitsOnly = trimmed.replace(/\D/g, '');
  // Reject country-code-only / incomplete numbers (need a plausible subscriber number)
  if (digitsOnly.length < 8) {
    return { isValid: false, error: 'Phone number is too short' };
  }
  if (digitsOnly.length > 15) {
    return { isValid: false, error: 'Phone number is too long' };
  }

  return { isValid: true };
};

/**
 * Validates a URL
 */
export const validateUrl = (
  url: string,
  required: boolean = false
): ValidationResult => {
  if (!url || url.trim() === '') {
    if (required) {
      return { isValid: false, error: 'URL is required' };
    }
    return { isValid: true }; // Optional field
  }

  try {
    new URL(url);
    return { isValid: true };
  } catch {
    return { isValid: false, error: 'Please enter a valid URL' };
  }
};

/**
 * Validates a date
 */
export const validateDate = (
  date: string,
  required: boolean = false
): ValidationResult => {
  if (!date || date.trim() === '') {
    if (required) {
      return { isValid: false, error: 'Date is required' };
    }
    return { isValid: true }; // Optional field
  }

  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) {
    return { isValid: false, error: 'Please enter a valid date' };
  }

  return { isValid: true };
};

/**
 * Optional birth date: all empty is OK; any part requires a complete valid calendar date.
 */
export const validateOptionalDateParts = (
  parts: { day?: string; month?: string; year?: string },
  completeIso: string
): ValidationResult => {
  const day = parts.day ?? '';
  const month = parts.month ?? '';
  const year = parts.year ?? '';
  const hasAny = !!(day || month || year);
  if (!hasAny) {
    return { isValid: true };
  }
  if (!completeIso) {
    // Keep English canonical text so getValidationErrorMessage can map to register.dateIncomplete.
    return { isValid: false, error: 'Please enter a complete date of birth' };
  }
  // Incomplete parts that formed an invalid calendar combo (e.g. day cleared for month).
  const dayNum = parseInt(day, 10);
  const monthNum = parseInt(month, 10);
  if (day && month && !isNaN(dayNum) && !isNaN(monthNum) && dayNum > getDaysInMonth(monthNum, year ? parseInt(year, 10) : undefined)) {
    return { isValid: false, error: 'That day is not valid for the selected month. Please choose a valid day.' };
  }
  return validateDate(completeIso, false);
};

/**
 * Validates a year (4 digits)
 */
export const validateYear = (
  year: string | number,
  required: boolean = false
): ValidationResult => {
  const yearStr = typeof year === 'number' ? year.toString() : year;

  if (!yearStr || yearStr.trim() === '') {
    if (required) {
      return { isValid: false, error: 'Year is required' };
    }
    return { isValid: true }; // Optional field
  }

  const yearNum = parseInt(yearStr, 10);
  const currentYear = new Date().getFullYear();

  if (isNaN(yearNum)) {
    return { isValid: false, error: 'Please enter a valid year' };
  }

  if (yearNum < 1900 || yearNum > currentYear + 10) {
    return {
      isValid: false,
      error: `Year must be between 1900 and ${currentYear + 10}`,
    };
  }

  return { isValid: true };
};

/**
 * Validates a postal code (basic validation)
 */
export const validatePostalCode = (
  postalCode: string,
  required: boolean = false
): ValidationResult => {
  if (!postalCode || postalCode.trim() === '') {
    if (required) {
      return { isValid: false, error: 'Postal code is required' };
    }
    return { isValid: true }; // Optional field
  }

  // Basic validation - alphanumeric, 3-10 characters
  const postalCodeRegex = /^[A-Z0-9\s-]{3,10}$/i;
  if (!postalCodeRegex.test(postalCode)) {
    return { isValid: false, error: 'Please enter a valid postal code' };
  }

  return { isValid: true };
};

/**
 * Validates text length
 */
export const validateLength = (
  value: string,
  minLength?: number,
  maxLength?: number,
  required: boolean = false
): ValidationResult => {
  if (!value || value.trim() === '') {
    if (required) {
      return { isValid: false, error: 'This field is required' };
    }
    return { isValid: true }; // Optional field
  }

  const length = value.trim().length;

  if (minLength !== undefined && length < minLength) {
    return {
      isValid: false,
      error: `Must be at least ${minLength} characters`,
    };
  }

  if (maxLength !== undefined && length > maxLength) {
    return {
      isValid: false,
      error: `Must be no more than ${maxLength} characters`,
    };
  }

  return { isValid: true };
};

/**
 * Validates a number range
 */
export const validateNumberRange = (
  value: string | number,
  min?: number,
  max?: number,
  required: boolean = false
): ValidationResult => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    if (required) {
      return { isValid: false, error: 'This field is required' };
    }
    return { isValid: false, error: 'Please enter a valid number' };
  }

  if (min !== undefined && numValue < min) {
    return { isValid: false, error: `Must be at least ${min}` };
  }

  if (max !== undefined && numValue > max) {
    return { isValid: false, error: `Must be no more than ${max}` };
  }

  return { isValid: true };
};

/**
 * Validates that a value is selected from options
 */
export const validateSelection = (
  value: string,
  options: { value: string; label: string }[],
  required: boolean = false
): ValidationResult => {
  if (!value || value.trim() === '') {
    if (required) {
      return { isValid: false, error: 'Please select an option' };
    }
    return { isValid: true }; // Optional field
  }

  const isValidOption = options.some(opt => opt.value === value);
  if (!isValidOption) {
    return { isValid: false, error: 'Please select a valid option' };
  }

  return { isValid: true };
};

/**
 * Validates a file
 */
export const validateFile = (
  file: File | null,
  maxSize?: number, // in bytes
  acceptedTypes?: string[],
  required: boolean = false
): ValidationResult => {
  if (!file) {
    if (required) {
      return { isValid: false, error: 'File is required' };
    }
    return { isValid: true }; // Optional field
  }

  if (maxSize && file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    return {
      isValid: false,
      error: `File size must be less than ${maxSizeMB}MB`,
    };
  }

  if (acceptedTypes && acceptedTypes.length > 0) {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const mimeType = file.type;

    const isAccepted = acceptedTypes.some(type => {
      const normalizedType = type.toLowerCase().replace('.', '');
      return (
        fileExtension === normalizedType || mimeType.includes(normalizedType)
      );
    });

    if (!isAccepted) {
      return {
        isValid: false,
        error: `File type must be one of: ${acceptedTypes.join(', ')}`,
      };
    }
  }

  return { isValid: true };
};

export type BilingualLastNameErrorKey =
  | 'register.lastNameRequired'
  | 'register.lastNameEnglishRequired'
  | 'register.lastNameGreekRequired';

/** Both English and Greek last names are required. */
export const bilingualLastNameErrorKey = (
  lastNameEn?: string,
  lastNameGr?: string
): BilingualLastNameErrorKey | undefined => {
  const hasEn = !!lastNameEn?.trim();
  const hasGr = !!lastNameGr?.trim();
  if (hasEn && hasGr) return undefined;
  if (!hasEn && !hasGr) return 'register.lastNameRequired';
  if (!hasEn) return 'register.lastNameEnglishRequired';
  return 'register.lastNameGreekRequired';
};

/**
 * Combines multiple validation results
 */
export const combineValidations = (
  ...results: ValidationResult[]
): ValidationResult => {
  for (const result of results) {
    if (!result.isValid) {
      return result;
    }
  }
  return { isValid: true };
};
